"use strict";

import React, { Component } from "react";
import SignatureCanvas from "react-signature-canvas";

export default class extends Component {
  sigPad = null;

  render() {
    const { className, id, ...props } = this.props;
    return React.createElement(SignatureCanvas, {
      penColor: "black",
      ...props,
      ref: (ref) => {
        this.sigPad = ref;
        if (ref) {
          // The PureScript modal only knows the canvas id. Store the signature
          // pad on the canvas so the Clear button can clear both the pixels and
          // the pad's internal stroke data.
          ref.getCanvas().__signaturePad = ref;
        }
      },
      canvasProps: {
        className: className ? className : "",
        id,
      },
      clearOnResize: false,
    });
  }

  componentDidMount() {
    // If we were given an initial signature to render, draw it on the canvas.
    if (this.props.sigBlob) {
      // Convert the blob into a data URL (base-64 string).
      const dataUrl = URL.createObjectURL(this.props.sigBlob);
      // Draw that on the canvas.
      this.sigPad.fromDataURL(dataUrl);
    }
    window.addEventListener("resize", this.onResize);
  }

  componentWillUnmount() {
    if (this.sigPad) {
      delete this.sigPad.getCanvas().__signaturePad;
    }
    window.removeEventListener("resize", this.onResize);
  }

  onResize = (_) => {
    resizeCanvas(this.sigPad);
  };
}

export async function getCanvasBlobFromId(canvasId) {
  const canvas = document.getElementById(canvasId);
  return await new Promise((resolve) => cropSignatureCanvas(resolve, canvas));
}

export const clearCanvasFromId = (canvasId) => () => {
  const canvas = document.getElementById(canvasId);
  if (!canvas) {
    return;
  }

  const sigPad = canvas.__signaturePad;
  if (sigPad) {
    sigPad.clear();
    return;
  }

  const context = canvas.getContext("2d");
  if (context) {
    context.clearRect(0, 0, canvas.width, canvas.height);
  }
};

export const canvasHasInkFromId = (canvasId) => () => {
  const canvas = document.getElementById(canvasId);
  return countCanvasInkPixels(canvas) > 0;
};

function resizeCanvas(sigPad) {
  const canvas = sigPad.getCanvas();
  if (canvas.offsetWidth != 0 && canvas.offsetHeight != 0) {
    // Get the signature as a list of points.
    const data = sigPad.toData();

    // Do the resize.
    // From example here: https://github.com/szimek/signature_pad#handling-high-dpi-screens
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    // Clear the contents of the pad and re-draw the signature from before.
    sigPad.clear();
    sigPad.fromData(data);
  }
}

function countCanvasInkPixels(canvas) {
  if (!canvas) {
    return 0;
  }

  const context = canvas.getContext("2d");
  if (!context || canvas.width <= 0 || canvas.height <= 0) {
    return 0;
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  let inkPixels = 0;
  for (let index = 3; index < imageData.data.length; index += 4) {
    if (imageData.data[index] > 0) {
      inkPixels += 1;
    }
  }
  return inkPixels;
}

function cropSignatureCanvas(resolve, canvas) {
  // First duplicate the canvas to not alter the original
  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  croppedCanvas.width = canvas.width;
  croppedCanvas.height = canvas.height;
  croppedCtx.drawImage(canvas, 0, 0);

  // Next do the actual cropping
  let w = croppedCanvas.width;
  let h = croppedCanvas.height;
  const pix = { x: [], y: [] };
  const imageData = croppedCtx.getImageData(0, 0, croppedCanvas.width, croppedCanvas.height);
  let x;
  let y;
  let index;

  for (y = 0; y < h; y++) {
    for (x = 0; x < w; x++) {
      index = (y * w + x) * 4;
      if (imageData.data[index + 3] > 0) {
        pix.x.push(x);
        pix.y.push(y);
      }
    }
  }
  pix.x.sort(function (a, b) {
    return a - b;
  });
  pix.y.sort(function (a, b) {
    return a - b;
  });
  const n = pix.x.length - 1;

  w = pix.x[n] - pix.x[0];
  h = pix.y[n] - pix.y[0];
  const cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

  croppedCanvas.width = w;
  croppedCanvas.height = h;
  croppedCtx.putImageData(cut, 0, 0);

  return croppedCanvas.toBlob(resolve, "image/png");
}
